<template>
  <Transition name="toast">
    <div
      v-if="visible"
      :class="toastClasses"
      role="alert"
      aria-live="polite"
    >
      <div class="toast-icon">
        <i :class="iconClass"></i>
      </div>
      <div class="toast-content">
        <p v-if="title" class="toast-title">{{ title }}</p>
        <p class="toast-message">{{ message }}</p>
      </div>
      <button
        v-if="closable"
        class="toast-close"
        @click="close"
        aria-label="Cerrar notificación"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';

interface Props {
  title?: string;
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  closable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  type: 'info',
  duration: 3000,
  closable: true,
});

const emit = defineEmits<{
  close: [];
}>();

const visible = ref(false);
let timer: ReturnType<typeof setTimeout> | null = null;

const toastClasses = computed(() => {
  const baseClasses = [
    'toast',
    'flex',
    'items-start',
    'gap-3',
    'p-4',
    'rounded-lg',
    'shadow-xl',
    'min-w-[300px]',
    'max-w-[500px]',
    'backdrop-blur-sm',
  ];

  const typeClasses = {
    success: ['bg-green-50', 'border-l-4', 'border-green-500'],
    error: ['bg-red-50', 'border-l-4', 'border-red-500'],
    warning: ['bg-yellow-50', 'border-l-4', 'border-yellow-500'],
    info: ['bg-blue-50', 'border-l-4', 'border-blue-500'],
  };

  return [...baseClasses, ...typeClasses[props.type]].join(' ');
});

const iconClass = computed(() => {
  const icons = {
    success: 'fas fa-check-circle text-green-600',
    error: 'fas fa-exclamation-circle text-red-600',
    warning: 'fas fa-exclamation-triangle text-yellow-600',
    info: 'fas fa-info-circle text-blue-600',
  };
  return icons[props.type];
});

const close = () => {
  visible.value = false;
  if (timer) clearTimeout(timer);
  setTimeout(() => emit('close'), 300);
};

onMounted(() => {
  visible.value = true;
  
  if (props.duration > 0) {
    timer = setTimeout(() => {
      close();
    }, props.duration);
  }
});
</script>

<style scoped>
.toast {
  position: relative;
}

.toast-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: 1.25rem;
}

.toast-content {
  flex: 1;
}

.toast-title {
  font-weight: var(--font-weight-semibold);
  margin: 0 0 0.25rem 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
}

.toast-message {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-relaxed);
}

.toast-close {
  flex-shrink: 0;
  background: none;
  border: none;
  color: var(--color-neutral-500);
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-sm);
  transition: var(--transition-colors);
}

.toast-close:hover {
  color: var(--color-neutral-700);
  background-color: var(--color-neutral-200);
}

.toast-enter-active {
  animation: slideInRight 0.3s var(--ease-out);
}

.toast-leave-active {
  animation: slideOutRight 0.3s var(--ease-in);
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideOutRight {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100%);
  }
}
</style>

